import { useRef, useState } from 'react';
import axios from 'axios';
import { Upload, Loader2, Check, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Reusable image/video uploader.
// Uploads via POST /api/admin/upload (multipart) and calls onUploaded(url).
export default function FileUploader({ testId, currentUrl, onUploaded, accept = 'image/*', label = 'Upload image' }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFile = async (file) => {
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/admin/upload`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = `${BACKEND_URL}${res.data.url}`;
      onUploaded(url);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3">
        {currentUrl ? (
          <div className="relative w-20 h-20 rounded-lg overflow-hidden border" style={{ borderColor: '#E5E7EB' }}>
            {accept.startsWith('video') ? (
              <video src={currentUrl} className="w-full h-full object-cover" />
            ) : (
              <img src={currentUrl} alt="preview" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
            )}
          </div>
        ) : (
          <div className="w-20 h-20 rounded-lg border flex items-center justify-center text-xs" style={{ borderColor: '#E5E7EB', color: '#9CA3AF' }}>
            No file
          </div>
        )}
        <div className="flex-1">
          <input
            ref={inputRef}
            data-testid={testId}
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => handleFile(e.target.files[0])}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all hover:opacity-80 disabled:opacity-60"
            style={{ backgroundColor: '#EAF2EC', color: '#0A3F22' }}
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? 'Uploading...' : label}
          </button>
          {currentUrl && (
            <button
              type="button"
              onClick={() => onUploaded('')}
              className="ml-2 inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
              style={{ backgroundColor: '#FEF2F2', color: '#DC2626' }}
            >
              <X size={12} /> Remove
            </button>
          )}
          {currentUrl && !error && !uploading && (
            <div className="text-xs mt-1.5 flex items-center gap-1" style={{ color: '#059669' }}>
              <Check size={11} /> File attached
            </div>
          )}
          {error && <div className="text-xs mt-1.5" style={{ color: '#DC2626' }}>{error}</div>}
        </div>
      </div>
    </div>
  );
}
